import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAxios } from "../../../api/axios";
import { useTranslation } from "react-i18next";
import { useLocation, useSearchParams } from "react-router-dom";
import useProperty from "../../context/useProperty";

export const useLikeProperty = (
  loginType: string,
  onSuccess?: any,
  onError?: any
) => {
  const axios = useAxios(loginType);
  const { t } = useTranslation();
  const urlKey = t("add-like-property-api");
  const queryClient = useQueryClient();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { coords }: any = useProperty();
  const queryParams = new URLSearchParams(location.search);
  // const panel = Number(queryParams.get('panel'));
  const page = Number(queryParams.get("page")) || 1;
  const limit = Number(queryParams.get("limit")) || 10;
  const searchTextValue = searchParams.get("searchText");
  const stateValue = searchParams.get("state");
  const offMarketValue = searchParams.get("offMarket");
  const occupancyValue = searchParams.get("occupancy");
  const salesInstructionsValue = searchParams.get("salesInstructions");
  const statusValue = searchParams.get("status");
  const possessionDeliveryValue = searchParams.get("possessionDelivery");
  const sortValue = searchParams.get("sort");
  const orderValue = searchParams.get("order");

  // Update the mutationFn type to include keyId
  const likePropertyMutation: any = useMutation({
    mutationFn: async (data: { propertyId: string }) => {
      const res = await axios.post(urlKey, { property_id: data.propertyId });
      return res;
    },
    onSuccess: async (
      data,
      variables: { propertyId: string; keyId: string }
    ) => {
      const propertyId = variables.propertyId;
      let payload: any = {
        listType: "map",
        mapBounds: coords,
        state: stateValue || "CA",
        ...(searchTextValue && { searchText: searchTextValue })
      };
      if (searchTextValue) {
        payload = { ...payload, searchTxt: searchTextValue };
      }
      if (stateValue) {
        payload = { ...payload, state: stateValue };
      }
      if (offMarketValue) {
        payload = { ...payload, offMarket: offMarketValue === "Yes" };
      }
      if (occupancyValue) {
        payload = { ...payload, occupancy: occupancyValue };
      }
      if (salesInstructionsValue) {
        payload = { ...payload, salesInstruction: salesInstructionsValue };
      }
      if (statusValue) {
        payload = { ...payload, status: statusValue };
      }
      if (possessionDeliveryValue) {
        payload = { ...payload, possession: possessionDeliveryValue };
      }
      if (sortValue) {
        payload = { ...payload, orderBy: sortValue };
      }
      if (orderValue) {
        payload = { ...payload, order: orderValue };
      }
      await queryClient.setQueryData(
        ["MapPublishProperties", payload],
        (oldData: any) => {
          if (!oldData) return oldData;
          const updatedData = oldData.data.map((property: any) =>
            property.id === propertyId ? { ...property, favCount: 1 } : property
          );

          return { ...oldData, data: updatedData };
        }
      );

      await queryClient.setQueryData(
        ["MapInvestorProperties", payload],
        (oldData: any) => {
          if (!oldData) return oldData;
          const updatedData = oldData.data.map((property: any) =>
            property.id === propertyId ? { ...property, favCount: 1 } : property
          );

          return { ...oldData, data: updatedData };
        }
      );

      // Update Recent-Properties
      await queryClient.setQueryData(
        [`Recent-Properties`, page, limit],
        (oldData: any) => {
          if (!oldData) return oldData;
          const updatedData = oldData.data.map((property: any) =>
            property.id === propertyId ? { ...property, favCount: 1 } : property
          );
          return { ...oldData, data: updatedData };
        }
      );

      await queryClient.setQueryData(
        [`Recent-Properties-infinite-list`],
        (oldData: any) => {
          if (!oldData) return oldData;
          const updatedPages = oldData.pages.map((page: any) =>
            page.map((property: any) =>
              property.id === propertyId
                ? { ...property, favCount: 1 }
                : property
            )
          );
          return { ...oldData, pages: updatedPages };
        }
      );

      await queryClient.setQueryData(
        [`Saved-Properties-infinite-list`],
        (oldData: any) => {
          if (!oldData) return oldData;
          const updatedPages = oldData.pages.map((page: any) =>
            page.map((property: any) =>
              property.id === propertyId
                ? { ...property, favCount: 1 }
                : property
            )
          );
          return { ...oldData, pages: updatedPages };
        }
      );

      await queryClient.setQueryData(
        [`Your-Bid-infinite-list`],
        (oldData: any) => {
          if (!oldData) return oldData;
          const updatedPages = oldData.pages.map((page: any) =>
            page.map((property: any) =>
              property.id === propertyId
                ? { ...property, favCount: 1 }
                : property
            )
          );
          return { ...oldData, pages: updatedPages };
        }
      );

      await queryClient.setQueryData(
        ["Your-Bids", page, limit],
        (oldData: any) => {
          if (!oldData) return oldData;
          const updatedData = oldData.data.map((property: any) =>
            property.id === propertyId ? { ...property, favCount: 1 } : property
          );
          return { ...oldData, data: updatedData };
        }
      );
      await queryClient.setQueryData(
        [`investor-property-list`, coords, searchTextValue, sortValue],
        (oldData: any) => {
          if (!oldData) return oldData;

          // Since infinite query returns an array of pages, we need to update the correct page
          const updatedPages = oldData.pages.map((page: any) => {
            const updatedProperties = page.data.map((property: any) =>
              property.id === propertyId
                ? { ...property, favCount: 1 }
                : property
            );
            return { ...page, data: updatedProperties };
          });

          return { ...oldData, pages: updatedPages };
        }
      );
      // Update Property-List
      await queryClient.setQueryData(
        [
          `Property-List`,
          coords,
          searchTextValue,
          stateValue,
          offMarketValue,
          occupancyValue,
          salesInstructionsValue,
          statusValue,
          possessionDeliveryValue,
          sortValue
        ],
        (oldData: any) => {
          if (!oldData) return oldData;

          // Since infinite query returns an array of pages, we need to update the correct page
          const updatedPages = oldData.pages.map((page: any) => {
            const updatedProperties = page.data.map((property: any) =>
              property.id === propertyId
                ? { ...property, favCount: 1 }
                : property
            );
            return { ...page, data: updatedProperties };
          });

          return { ...oldData, pages: updatedPages };
        }
      );

      // Update Property-Detail
      await queryClient.setQueryData(
        [`Property-Detail`, propertyId],
        (oldData: any) => {
          if (!oldData) return oldData;
          const updatedData = { ...oldData.data, favCount: 1 };
          return { ...oldData, data: updatedData };
        }
      );

      // Update Related-Property-List
      // await queryClient.setQueryData(
      //   [`Related-Property-List`, propertyId],
      //   (oldData: any) => {
      //     if (!oldData) return oldData;
      //     const updatedData = oldData.data.map((property: any) =>
      //       property.id === propertyId ? { ...property, favCount: 1 } : property
      //     );
      //     return { ...oldData, data: updatedData };
      //   }
      // );
      // await setHoveredProperty({ ...hoveredProperty, favCount: 1 });
      if (onSuccess) {
        onSuccess(data);
      }
    },
    onError: (err) => {
      if (onError) {
        onError(err);
      }
    }
  });

  // likeProperty only passes propertyId and keyId to the mutation, not the API request
  async function likeProperty(e: any, propertyId: string, keyId: string) {
    e.stopPropagation();
    // Only send propertyId to the API request, but keep both values for success handling
    likePropertyMutation.mutate({ propertyId, keyId });
  }

  return { likeProperty };
};
